import {
  PURCHASE_ORDER_DOCUMENT_EXTENSIONS,
  parseAmountToCents,
  purchaseOrderDocumentProblem,
  type ResellerCustomer,
} from "@xop-any-time/shared";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
} from "@mui/material";
import type { TextFieldProps } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileUp, Paperclip } from "lucide-react";
import { useRef, useState, type JSX } from "react";
import { useNavigate } from "react-router-dom";

import { BackButton } from "../../../components/ui/back-button.js";
import { keys, listCustomers } from "../../../lib/crmApi.js";
import { createPurchaseOrder, purchaseOrderKeys, todayLocal } from "../../../lib/purchaseOrdersApi.js";
import { formatBytes } from "../../knowledge-base/utils.js";
import { PageHeader } from "../crm.js";
import { PURCHASE_ORDER_EXPLAINER } from "./purchase-order-ui.js";

/**
 * "Add purchase order" — upload the reseller's OWN purchase order (2026-09-17).
 *
 * For an order that did not start as an XOP quote, or that the reseller's
 * purchasing system produced anyway. The document is the order, so it is the
 * one required field; everything else labels it. crm-api forces the reseller
 * to the actor's own company and refuses a customer that is not one of its
 * own ("That customer is not one of yours."), and this page shows that
 * sentence as it arrives.
 *
 * The file rule (PDF, image, Word, Excel; ≤ 10 MB) is checked HERE first, so
 * a reseller hears "too big" before sitting through an upload — and again by
 * crm-api, which is the one that counts.
 */

export function NewPurchaseOrderPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [fileProblem, setFileProblem] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [poNumber, setPoNumber] = useState("");
  const [orderDate, setOrderDate] = useState(() => todayLocal());
  const [total, setTotal] = useState("");
  const [note, setNote] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [customerSearch, setCustomerSearch] = useState("");
  const customersQuery = useQuery({
    queryKey: keys.customers({ page: 1, search: customerSearch }),
    queryFn: () => listCustomers({ page: 1, search: customerSearch }),
  });
  const customers: ResellerCustomer[] = customersQuery.data?.customers ?? [];
  const selectedCustomer = customers.find((c) => c.id === customerId) ?? null;

  const totalCents = parseAmountToCents(total);
  const totalProblem = totalCents === undefined ? "Enter the total as an amount, like 1250.00." : null;
  const missingFile = file === null ? "Attach your purchase order document." : null;

  const create = useMutation({
    mutationFn: () =>
      createPurchaseOrder({
        document: file as File,
        poNumber,
        orderDate,
        ...(customerId !== null ? { customerId } : {}),
        ...(typeof totalCents === "number" ? { totalCents } : {}),
        note,
      }),
    onSuccess: (po) => {
      void queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all });
      navigate(`/purchase-orders/${po.id}`);
    },
  });

  const pickFile = (picked: File | undefined): void => {
    if (!picked) return;
    const problem = purchaseOrderDocumentProblem(picked);
    setFileProblem(problem);
    setFile(problem === null ? picked : null);
  };

  const submit = (): void => {
    setSubmitted(true);
    if (missingFile || fileProblem || totalProblem) return;
    create.mutate();
  };

  const fileMessage = fileProblem ?? (submitted ? missingFile : null);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <BackButton to="/purchase-orders" label="Back to purchase orders" />
      </Box>

      <PageHeader title="Add purchase order" description={PURCHASE_ORDER_EXPLAINER} />

      {create.error && (
        <Alert severity="error" data-testid="new-po-error">
          {(create.error as Error).message}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700 }}>
            Your purchase order document
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <input
              ref={fileInput}
              type="file"
              hidden
              data-testid="po-document-input"
              accept={[...PURCHASE_ORDER_DOCUMENT_EXTENSIONS, "application/pdf", "image/*"].join(",")}
              onChange={(e) => {
                pickFile(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              variant="outlined"
              startIcon={<Paperclip size={16} />}
              disabled={create.isPending}
              onClick={() => fileInput.current?.click()}
            >
              {file ? "Choose a different file" : "Choose file"}
            </Button>
            {file && (
              <Typography data-testid="po-document-name" sx={{ fontSize: 13.5 }}>
                {file.name} · {formatBytes(file.size)}
              </Typography>
            )}
          </Box>
          <Typography
            data-testid="po-document-hint"
            role={fileMessage ? "alert" : undefined}
            sx={{ fontSize: 12.5, color: fileMessage ? "error.main" : "text.secondary", mt: -1.5 }}
          >
            {fileMessage ?? "Required. A PDF, an image, a Word or an Excel file, up to 10 MB."}
          </Typography>

          <Autocomplete
            size="small"
            options={customers}
            value={selectedCustomer}
            loading={customersQuery.isPending}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onInputChange={(_e, value, reason) => {
              if (reason === "input") setCustomerSearch(value);
            }}
            onChange={(_e, option) => setCustomerId(option?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...(params as TextFieldProps)}
                label="Customer (optional)"
                placeholder="Search your customers…"
                inputProps={{ ...params.inputProps, "aria-label": "Customer" }}
                helperText="Which of your customers this order is for, if it is for one."
              />
            )}
          />

          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" } }}>
            <TextField
              size="small"
              label="Your PO number"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              inputProps={{ maxLength: 100, "aria-label": "Your PO number" }}
            />
            <TextField
              size="small"
              type="date"
              label="Order date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ "aria-label": "Order date" }}
            />
            <TextField
              size="small"
              label="Total (optional)"
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              error={totalProblem !== null}
              helperText={totalProblem ?? "As it appears on your order."}
              inputProps={{ inputMode: "decimal", "aria-label": "Total" }}
            />
          </Box>

          <TextField
            multiline
            minRows={3}
            size="small"
            label="Note for XOP Networks (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            inputProps={{ maxLength: 2000, "aria-label": "Note for XOP Networks" }}
          />

          <Box>
            <Button
              variant="contained"
              startIcon={<FileUp size={16} />}
              disabled={create.isPending}
              onClick={submit}
            >
              {create.isPending ? "Sending…" : "Send purchase order"}
            </Button>
          </Box>

          <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>
            XOP Networks is emailed your purchase order, and you receive a copy.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default NewPurchaseOrderPage;
